import Vue from "vue";
import Vuex from "vuex";
import moment from "moment-timezone";
import md5 from "crypto-js/md5";
import Api from "@/services/Api.service.js";
import { locations } from "@/assets/json/locations.json";
import { UK_AIRPORTS } from "@/services/UKAirports.service.js";

/* global invsy */

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    invsyUser: null,
    isIframe: false,
    loaderOpen: false,
    points: 0,
    flags: {
      flightReview: false,
      isDomesticReturn: false,
      searchInProgress: false,
      allSelected: false,
      showReturnFlights: false,
    },
    urlSearchParameters: undefined,
    searchParameters: {},
    searchResults: {
      outbound: [],
      return: [],
      errors: [],
    },
    priorSearch: {
      exists: false,
      arrivalLocation: {
        airportName: undefined,
        airportCode: undefined,
      },
      departureLocation: {
        airportName: undefined,
        airportCode: undefined,
      },
      departureTime: "Any time",
      adults: 1,
      youngAdults: 0,
      children: 0,
      infants: 0,
      preferredCabin: "Economy",
      departureDate: undefined,
      returnDate: undefined,
      multiFlights: [],
      tripType: "return",
    },
    resultConstants: {
      marketingAirlines: [],
      minOutboundTime: 0,
      maxOutboundTime: 0,
      minReturnTime: 0,
      maxReturnTime: 0,
      minPrice: 0,
      maxPrice: 0,
      minTravelTime: 0,
      maxTravelTime: 0,
    },
    filteredOutbound: [],
    outbound: [],
    return: [],
    filteredReturn: [],
    selectedFlights: {
      outbound: {},
      return: {},
    },
    payer: {},
    passengers: {
      adults: [],
      youngAdults: [],
      children: [],
      infants: [],
    },
    allFlights: undefined,
    departureTimezone: undefined,
    arrivalTimezone: undefined,
    extraDetails: {},
    countries: [],
    priceResponse: {},
    products: [],
    baggageCost: 0,
    paymentCharge: 0,
    paymentMethodResponse: {},
    paymentOptions: [],
    cardDetails: undefined,
    bookingResponse: {},
    validationErrors: [],
    pnpReferenceNumber: undefined,
    currentBlock: 1,
    session: generateRandomID(4),
    bookBody: {},
    authBookingResult: {},
    isInternational: false,
    addbaggage: false,
    additionalBaggageAllowed: false,
    specialFareWarningSeen: false,
    ukTransitWarningSeen: false,
    noBaggageWarningSeen: false,
    allAirports: locations,
    isApiDown: false,
    apiErrorCode: 0,
  },
  getters: {
    getAllAirports: (state) => state.allAirports,
    taggedOutboundFlights: (state) => {
      const isInternational = state.isInternational;

      if (!isInternational || !state.filteredOutbound) {
        return state.filteredOutbound || [];
      }

      return sortTaggedFlights(state.filteredOutbound);
    },
    // Emirates H-class special fare
    isEmiratesBusinessHClass: () => (flight) => {
      return (
        flight.airlineCode === "EK" &&
        flight.cabinClass === "BUSINESS" &&
        flight.bookingClass === "H"
      );
    },
    hasEmiratesHClassFlights: (_state, getters) => (flights) => {
      return flights.some((flight) => getters.isEmiratesBusinessHClass(flight));
    },
    hasVerteilEmiratesSpecialFare: (state, getters) => {
      if (
        !state.selectedFlights.outbound ||
        !state.selectedFlights.outbound.odoList
      ) {
        return false;
      }

      return (
        state.selectedFlights.outbound.odoList[0].companyName === "Verteil" &&
        state.selectedFlights.outbound.odoList[0].segments.some((segment) =>
          getters.isEmiratesBusinessHClass(segment)
        )
      );
    },
    hasUKTransit: (state) => {
      const segments = [];

      const appendSegments = (odoList) => {
        if (!odoList || !Array.isArray(odoList)) return;
        odoList.forEach((odo) => {
          if (odo.segments && Array.isArray(odo.segments)) {
            segments.push(...odo.segments);
          }
        });
      };

      appendSegments(
        state.selectedFlights.outbound && state.selectedFlights.outbound.odoList
      );
      appendSegments(
        state.selectedFlights.return && state.selectedFlights.return.odoList
      );

      if (segments.length === 0) {
        return false;
      }

      const firstOrigin = segments[0] && segments[0].origCode;
      const lastDestination =
        segments[segments.length - 1] && segments[segments.length - 1].destCode;

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const destCode = segment && segment.destCode;
        const origCode = segment && segment.origCode;

        if (
          destCode &&
          UK_AIRPORTS.includes(destCode) &&
          destCode !== lastDestination
        ) {
          return true;
        }

        if (
          origCode &&
          UK_AIRPORTS.includes(origCode) &&
          origCode !== firstOrigin
        ) {
          return true;
        }
      }

      return false;
    },
  },
  mutations: {
    setApiDown(state, errorCode) {
      state.isApiDown = true;
      state.apiErrorCode = errorCode;
    },
    setApiHealthy(state) {
      state.isApiDown = false;
      state.apiErrorCode = 0;
    },
    setAdditionalBaggageAllowed(state, value) {
      state.additionalBaggageAllowed = value;
    },
    setSpecialFareWarningSeen(state, value) {
      state.specialFareWarningSeen = value;
    },
    setUKTransitWarningSeen(state, value) {
      state.ukTransitWarningSeen = value;
    },
    setNoBaggageWarningSeen(state, value) {
      state.noBaggageWarningSeen = value;
    },
    resetShowReturnFlightsFlag(state) {
      state.flags.showReturnFlights = false;
    },
    setInvsyUser(state, user) {
      state.invsyUser = user;
      state.isIframe = true;
    },
    setPoints(state, points) {
      state.points = points;
    },
    checkLoginKey(state, loginKey) {
      const date = moment().format("YYYYMMDD_");
      const hash = md5(date);
      if (loginKey == hash.toString()) {
        state.login = true;
      } else {
        if (process.env.VUE_APP_REDIRECT_ON_NO_LOGIN_KEY == "true") {
          window.open("https://www.investectravel.com/", "_self");
        } else {
          state.login = true;
        }
      }
    },
    saveBookBody(state, body) {
      state.bookBody = body;
    },
    recordSearch(state, search) {
      state.priorSearch = search;
    },
    setLoader(state, bool) {
      state.loaderOpen = bool;
    },
    setAddBaggage(state, bool) {
      state.addbaggage = bool;
    },
    setPnpReferenceNumber(state, ref) {
      state.pnpReferenceNumber = ref;
    },
    setAuthBookingResult(state, res) {
      state.authBookingResult = res;
    },
    setAllFlights(state, flights) {
      state.allFlights = flights;
    },
    setUrlSearchParameters(state, params) {
      state.urlSearchParameters = params;
    },
    updateBlock(state, block) {
      state.currentBlock = block;
    },
    resetSession(state) {
      state.session = generateRandomID(4);
    },
    updatePaymentCharge(state, cost) {
      state.paymentCharge = cost;
    },
    setCountries(state, countries) {
      state.countries = countries.data;
    },
    toggleFlightReview(state) {
      state.flags.flightReview = !state.flags.flightReview;
    },
    setSearchParameters(state, searchParameters) {
      state.searchParameters = searchParameters;
    },
    setPaymentMethodResponse(state, paymentMethods) {
      state.paymentMethodResponse = paymentMethods;
      state.paymentOptions = paymentMethods.paymentOptions;
    },
    setExtraDetails(state, extraDeatils) {
      state.extraDetails = extraDeatils;
    },
    setCardDetails(state, details) {
      state.cardDetails = details;
    },
    setBookingResponse(state, response) {
      state.bookingResponse = response;
      if (response.validationResults) {
        if (
          response.validationResults.passengersValidationResult &&
          response.validationResults.passengersValidationResult.invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.passengersValidationResults
                .invalidFields
            )
          );
        }
        if (
          response.validationResults.contactDetailsValidationResults &&
          response.validationResults.contactDetailsValidationResults
            .invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.contactDetailsValidationResults
                .invalidFields
            )
          );
        }
        if (
          response.validationResults.productsDetailsValidationResults &&
          response.validationResults.productsDetailsValidationResults
            .invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.productsDetailsValidationResults
                .invalidFields
            )
          );
        }
        if (
          response.validationResults.paymentDetailsValidationResults &&
          response.validationResults.paymentDetailsValidationResults
            .invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.paymentDetailsValidationResults
                .invalidFields
            )
          );
        }
      }
    },
    setIsInternational(state, isInternational) {
      state.isInternational = isInternational;
    },
    setSearchResults(state, searchResults) {
      if (searchResults.errors !== null) {
        state.searchResults.errors = searchResults.errors;
        state.filteredOutbound = [];
        state.filteredReturn = [];
        state.outbound = [];
        state.searchResults.return = [];
      } else {
        let outbound = searchResults.isBundled
          ? searchResults.itineraries
          : searchResults.outboundItineraries;
        state.flags.isDomesticReturn = searchResults.inboundItineraries
          ? true
          : false;

        // Process outbound flights
        for (let i = 0; i < outbound.length; ++i) {
          outbound[i].key = generateRandomID(3);
          outbound[i].marketingAirlines = [];
          outbound[i].biggestTravelTime = 0;
          outbound[i].absoluteDepartureTime = moment(
            outbound[i].odoList[0].segments[0].departureDateTime
          ).format("HH:mm");
          outbound[i].formattedDepartureDate = moment(
            outbound[i].odoList[0].segments[0].departureDateTime
          ).format("dddd, MMMM Do YYYY");
          outbound[i].absoluteArrivalTime = moment(
            outbound[i].odoList[outbound[i].odoList.length - 1].segments[
              outbound[i].odoList[outbound[i].odoList.length - 1].segments
                .length - 1
            ].arrivalDateTime
          ).format("HH:mm");

          // find the price Range
          let currentNumber = Number(outbound[i].amount);
          if (state.resultConstants.minPrice == 0) {
            state.resultConstants.minPrice = currentNumber;
            state.resultConstants.maxPrice = currentNumber;
          } else if (currentNumber < state.resultConstants.minPrice) {
            state.resultConstants.minPrice = currentNumber;
          } else if (currentNumber > state.resultConstants.maxPrice) {
            state.resultConstants.maxPrice = currentNumber;
          }

          for (let leg of outbound[i].odoList) {
            // get leg stops
            leg.stops = leg.segments.length < 2 ? 0 : leg.segments.length - 1;
            // get departure and arrival times
            leg.departureTime = moment(
              leg.segments[0].departureDateTime
            ).format("HH:mm");
            leg.arrivalTime = moment(
              leg.segments[leg.segments.length - 1].arrivalDateTime
            ).format("HH:mm");
            let duration = moment.duration(leg.duration);
            let hours = parseInt(duration.asHours());
            let minutes = parseInt(duration.asMinutes()) % 60;
            leg.totalTimeNumber = hours;
            leg.totalTime = `${hours}h${minutes}m`;
            if (state.resultConstants.minTravelTime === 0) {
              state.resultConstants.minTravelTime = leg.duration;
              state.resultConstants.maxTravelTime = leg.duration;
            } else if (leg.duration > state.resultConstants.maxTravelTime) {
              state.resultConstants.maxTravelTime = leg.duration;
            } else if (leg.duration < state.resultConstants.minTravelTime) {
              state.resultConstants.minTravelTime = leg.duration;
            }
            if (outbound[i].biggestTravelTime < leg.duration) {
              outbound[i].biggestTravelTime = leg.duration;
            }

            // determine min and max departure times
            let last = leg.segments.length - 1;
            let outboundTime = leg.segments[0].departureDateTime.split("T")[1];
            let returnTime = leg.segments[last].departureDateTime.split("T")[1];
            if (state.resultConstants.minOutboundTime === 0) {
              state.resultConstants.minOutboundTime = outboundTime;
              state.resultConstants.maxOutboundTime = outboundTime;
            } else if (outboundTime < state.resultConstants.minOutboundTime) {
              state.resultConstants.minOutboundTime = outboundTime;
            } else if (outboundTime > state.resultConstants.maxOutboundTime) {
              state.resultConstants.maxOutboundTime = outboundTime;
            }
            if (state.resultConstants.minReturnTime === 0) {
              state.resultConstants.minReturnTime = returnTime;
              state.resultConstants.maxReturnTime = returnTime;
            } else if (returnTime < state.resultConstants.minReturnTime) {
              state.resultConstants.minReturnTime = returnTime;
            } else if (returnTime > state.resultConstants.maxReturnTime) {
              state.resultConstants.maxReturnTime = returnTime;
            }

            for (let segment of leg.segments) {
              segment.baggage = [];
              searchResults.baggageAllowanceInfos.forEach((info) => {
                if (info.correspondingSegmentIdList.includes(segment.ID)) {
                  segment.baggage.push({
                    name: info.description,
                    type: info.type,
                  });
                  if (
                    info.description.includes("No") ||
                    info.description.includes("+") ||
                    info.description.includes("next")
                  ) {
                    outbound[i].bagWarning = true;
                  }
                }
              });
              if (
                !state.resultConstants.marketingAirlines.includes(
                  segment.airlineCode
                )
              ) {
                state.resultConstants.marketingAirlines.push(
                  segment.airlineCode
                );
              }
              if (
                !outbound[i].marketingAirlines.includes(segment.airlineCode)
              ) {
                outbound[i].marketingAirlines.push(segment.airlineCode);
              }
            }
          }
        }

        // Apply flight tags for international flights only
        if (state.isInternational) {
          // Find cheapest flights from api response (with manual calculation fallback)
          const cheapestFlightIds = searchResults.cheapestOutBoundId || [];
          const cheapestFlights =
            cheapestFlightIds.length > 0
              ? outbound.filter((f) => cheapestFlightIds.includes(f.id))
              : outbound.filter(
                  (f) => f.amount === Math.min(...outbound.map((f) => f.amount))
                );

          // Get fastest flights from api response (with manual calculation fallback)
          const fastestFlightIds = searchResults.fastestOutBoundId || [];
          const fastestFlights =
            fastestFlightIds.length > 0
              ? outbound.filter((f) => fastestFlightIds.includes(f.id))
              : (() => {
                  const shortestDuration = Math.min(
                    ...outbound.map((f) => getTotalDurationMs(f))
                  );
                  return outbound.filter(
                    (f) => getTotalDurationMs(f) === shortestDuration
                  );
                })();

          // Apply tags to each flight
          outbound.forEach((flight) => {
            flight.isCheapest = false;
            flight.isFastest = false;
            flight.isRecommended = false; // Keep property but never set to true

            // // Handle cheapest tag old logic with recommended tag - keeping for future use
            // if (cheapestFlights.length > 1) {
            //   const sortedCheapest = [...cheapestFlights].sort((a, b) => {
            //     return getTotalDurationMs(a) - getTotalDurationMs(b);
            //   });
            //   flight.isCheapest =
            //     cheapestFlights.length > 1 &&
            //     flight.id === sortedCheapest[1].id;
            //   flight.isRecommended = flight.id === sortedCheapest[0].id;
            // } else if (cheapestFlights.length === 1) {
            //   flight.isCheapest = flight.id === cheapestFlights[0].id;
            //   flight.isRecommended = flight.id === cheapestFlights[0].id;
            // }

            // Handle cheapest tag without recommended tag logic
            // Always pick the first flight in the sorted cheapest array (by fastest duration)
            if (cheapestFlights.length > 0) {
              const sortedCheapest = [...cheapestFlights].sort((a, b) => {
                return getTotalDurationMs(a) - getTotalDurationMs(b);
              });
              flight.isCheapest = flight.id === sortedCheapest[0].id;
            }

            // Handle fastest tag logic
            if (fastestFlights.length > 1) {
              const sortedFastestByPrice = [...fastestFlights].sort((a, b) => {
                return a.amount - b.amount;
              });
              flight.isFastest = flight.id === sortedFastestByPrice[0].id;
            } else if (fastestFlights.length === 1) {
              flight.isFastest = flight.id === fastestFlights[0].id;
            }
          });
        }

        state.filteredOutbound = outbound;
        state.outbound = outbound;

        // Process return flights for domestic itineraries
        if (!searchResults.isBundled) {
          let inbound = searchResults.inboundItineraries;
          for (let i = 0; i < inbound.length; ++i) {
            inbound[i].key = generateRandomID(3);
            inbound[i].marketingAirlines = [];
            inbound[i].biggestTravelTime = 0;
            inbound[i].absoluteDepartureTime = moment(
              inbound[i].odoList[0].segments[0].departureDateTime
            ).format("HH:mm");
            inbound[i].formattedDepartureDate = moment(
              inbound[i].odoList[0].segments[0].departureDateTime
            ).format("dddd, MMMM Do YYYY");
            inbound[i].absoluteArrivalTime = moment(
              inbound[i].odoList[inbound[i].odoList.length - 1].segments[
                inbound[i].odoList[inbound[i].odoList.length - 1].segments
                  .length - 1
              ].arrivalDateTime
            ).format("HH:mm");

            // // find the price Range
            let currentNumber = Number(inbound[i].amount);
            if (state.resultConstants.minPrice == 0) {
              state.resultConstants.minPrice = currentNumber;
              state.resultConstants.maxPrice = currentNumber;
            } else if (currentNumber < state.resultConstants.minPrice) {
              state.resultConstants.minPrice = currentNumber;
            } else if (currentNumber > state.resultConstants.maxPrice) {
              state.resultConstants.maxPrice = currentNumber;
            }
            for (let leg of inbound[i].odoList) {
              // get leg stops
              leg.stops = leg.segments.length < 2 ? 0 : leg.segments.length - 1;
              // get departure and arrival times
              leg.departureTime = moment(
                leg.segments[0].departureDateTime
              ).format("HH:mm");
              leg.arrivalTime = moment(
                leg.segments[leg.segments.length - 1].arrivalDateTime
              ).format("HH:mm");
              // get total leg time
              let duration = moment.duration(leg.duration);
              let hours = parseInt(duration.asHours());
              let minutes = parseInt(duration.asMinutes()) % 60;
              leg.totalTimeNumber = hours;
              leg.totalTime = `${hours}h${minutes}m`;
              if (state.resultConstants.minTravelTime === 0) {
                state.resultConstants.minTravelTime = leg.duration;
                state.resultConstants.maxTravelTime = leg.duration;
              } else if (hours > state.resultConstants.maxTravelTime) {
                state.resultConstants.maxTravelTime = leg.duration;
              } else if (hours < state.resultConstants.minTravelTime) {
                state.resultConstants.minTravelTime = leg.duration;
              }
              if (inbound[i].biggestTravelTime < leg.duration) {
                inbound[i].biggestTravelTime = leg.duration;
              }

              // determine min and max departure times
              let last = leg.segments.length - 1;
              let outboundTime = leg.segments[0].departureDateTime.split(
                "T"
              )[1];
              let returnTime = leg.segments[last].departureDateTime.split(
                "T"
              )[1];
              if (state.resultConstants.minOutboundTime === 0) {
                state.resultConstants.minOutboundTime = outboundTime;
                state.resultConstants.maxOutboundTime = outboundTime;
              } else if (outboundTime < state.resultConstants.minOutboundTime) {
                state.resultConstants.minOutboundTime = outboundTime;
              } else if (outboundTime > state.resultConstants.maxOutboundTime) {
                state.resultConstants.maxOutboundTime = outboundTime;
              }
              if (state.resultConstants.minReturnTime === 0) {
                state.resultConstants.minReturnTime = returnTime;
                state.resultConstants.maxReturnTime = returnTime;
              } else if (returnTime < state.resultConstants.minReturnTime) {
                state.resultConstants.minReturnTime = returnTime;
              } else if (returnTime > state.resultConstants.maxReturnTime) {
                state.resultConstants.maxReturnTime = returnTime;
              }
              for (let segment of leg.segments) {
                segment.baggage = [];
                searchResults.baggageAllowanceInfos.forEach((info) => {
                  if (info.correspondingSegmentIdList.includes(segment.ID)) {
                    segment.baggage.push({
                      name: info.description,
                      type: info.type,
                    });
                    if (
                      info.description.includes("No") ||
                      info.description.includes("+") ||
                      info.description.includes("next")
                    ) {
                      inbound[i].bagWarning = true;
                    }
                  }
                });
                if (
                  !state.resultConstants.marketingAirlines.includes(
                    segment.airlineCode
                  )
                ) {
                  state.resultConstants.marketingAirlines.push(
                    segment.airlineCode
                  );
                }
                if (
                  !inbound[i].marketingAirlines.includes(segment.airlineCode)
                ) {
                  inbound[i].marketingAirlines.push(segment.airlineCode);
                }
              }
            }
          }
          state.filteredReturn = inbound;
          state.return = inbound;
        }
        state.searchResults = searchResults;
      }
    },
    setOutboundFlight(state, flight) {
      state.selectedFlights.outbound = flight;
      if (state.flags.isDomesticReturn) {
        !isEmpty(state.selectedFlights.return)
          ? (state.flags.allSelected = true)
          : (state.flags.allSelected = false);
      } else {
        state.flags.allSelected = true;
      }
    },
    setReturnFlight(state, flight) {
      state.selectedFlights.return = flight;
      if (!isEmpty(state.selectedFlights.outbound)) {
        state.flags.allSelected = true;
      }
    },
    setPriceResponse(state, response) {
      state.products = [];
      state.priceResponse = response;
      if (response.products) {
        state.products = response.products.map((product) => {
          console.log(product);
          return {
            id: product.id,
            selectedValue: false,
          };
        });
      }
      //update selectedFlights for checked baggage?
      if (response.refreshBags) {
        state.selectedFlights.outbound.odoList.forEach((i) => {
          i.segments.forEach((segment) => {
            if (segment.baggage.length > 0) {
              segment.baggage[0].name =
                response.itineraries[0].odoList[0].segments[0].baggageAllowance[1];
            }
          });
        });
        state.selectedFlights.outbound.amount = response.itineraries[0].amount;
        state.selectedFlights.outbound.hasCheckedBagsForAllSegments =
          response.itineraries[0].hasCheckedBagsForAllSegments;
        if (state.flags.isDomesticReturn) {
          state.selectedFlights.return.odoList.forEach((i) => {
            i.segments.forEach((segment) => {
              if (segment.baggage.length > 0) {
                segment.baggage[0].name =
                  response.itineraries[0].odoList[0].segments[0].baggageAllowance[1];
              }
            });
          });
          state.selectedFlights.return.amount = response.itineraries[1].amount;
          state.selectedFlights.return.hasCheckedBagsForAllSegments =
            response.itineraries[1].hasCheckedBagsForAllSegments;
        }
      }
    },
    setOutboundFlightMobile(state, flight) {
      state.selectedFlights.outbound = flight;
      state.flags.showReturnFlights = true;
      if (state.flags.isDomesticReturn) {
        !isEmpty(state.selectedFlights.return)
          ? (state.flags.flightReview = true)
          : (state.flags.flightReview = false);
        !isEmpty(state.selectedFlights.return)
          ? (state.flags.allSelected = true)
          : (state.flags.allSelected = false);
      } else {
        state.flags.flightReview = true;
        state.flags.allSelected = true;
      }
    },
    setReturnFlightMobile(state, flight) {
      state.selectedFlights.return = flight;
      if (!isEmpty(state.selectedFlights.outbound)) {
        state.flags.flightReview = true;
        state.flags.allSelected = true;
      }
    },
    isDomesticReturn(state) {
      state.flags.isDomesticReturn = true;
    },
    notDomesticReturn(state) {
      state.flags.isDomesticReturn = false;
    },
    resetSearchResults(state) {
      state.searchResults = {
        outbound: [],
        return: [],
        errors: [],
      };
      state.allSelected = false;
    },
    resetPriceResponse(state) {
      state.priceResponse = {};
    },
    resetPaymentMethodResponse(state) {
      state.paymentMethodResponse = {};
    },
    resetBookingResponse(state) {
      state.bookingResponse = {};
    },
    updateBaggageCost(state, cost) {
      state.baggageCost = cost;
    },
    resetBaggageCost(state) {
      state.baggageCost = 0;
    },
    updateProducts(state, products) {
      state.products.forEach((product) => {
        console.log(product);
        if (products.includes(product.id)) {
          product.selectedValue = true;
        } else {
          product.selectedValue = false;
        }
      });
    },
    resetSelectedFlights(state) {
      state.selectedFlights = {
        outbound: {},
        return: {},
      };
      state.flags.allSelected = false;
      state.flags.showReturnFlights = false;
    },
    resetResultConstants(state) {
      state.resultConstants = {
        marketingAirlines: [],
        minOutboundTime: 0,
        maxOutboundTime: 0,
        minReturnTime: 0,
        maxReturnTime: 0,
        minPrice: 0,
        maxPrice: 0,
        minTravelTime: 0,
        maxTravelTime: 0,
      };
    },
    setPassengers(state, passengers) {
      if (passengers.isYoungAdultsException) {
        passengers.adults.push(...passengers.youngAdults);
        passengers.adults.forEach((adult) => {
          adult.type = "ADULT";
        });
        passengers.youngAdults = [];
      }
      if (passengers.adults) {
        passengers.adults.forEach((passenger) => {
          passenger.errors = undefined;
          if (
            passenger.specialRequests &&
            Object.keys(passenger.specialRequests).length === 0
          ) {
            delete passenger.specialRequests;
          } else if (passenger.specialRequests.mealSelection === null) {
            delete passenger.specialRequests.mealSelection;
          }
        });
      }
      if (passengers.youngAdults) {
        passengers.youngAdults.forEach((passenger) => {
          passenger.errors = undefined;
          if (
            passenger.specialRequests &&
            Object.keys(passenger.specialRequests).length === 0
          ) {
            delete passenger.specialRequests;
          } else if (passenger.specialRequests.mealSelection === null) {
            delete passenger.specialRequests.mealSelection;
          }
        });
      }
      if (passengers.children) {
        passengers.children.forEach((passenger) => {
          passenger.errors = undefined;
          if (
            passenger.specialRequests &&
            Object.keys(passenger.specialRequests).length === 0
          ) {
            delete passenger.specialRequests;
          } else if (passenger.specialRequests.mealSelection === null) {
            delete passenger.specialRequests.mealSelection;
          }
        });
      }
      if (passengers.infants) {
        passengers.infants.forEach((passenger) => {
          passenger.errors = undefined;
          if (
            passenger.specialRequests &&
            Object.keys(passenger.specialRequests).length === 0
          ) {
            delete passenger.specialRequests;
          } else if (passenger.specialRequests.mealSelection === null) {
            delete passenger.specialRequests.mealSelection;
          }
        });
      }
      state.passengers.adults = passengers.adults;
      state.passengers.youngAdults = passengers.youngAdults;
      state.passengers.children = passengers.children;
      state.passengers.infants = passengers.infants;
    },
    setPayer(state, payer) {
      state.payer = payer;
    },
    toggleSearchInProgress(state, progress) {
      state.flags.searchInProgress = progress;
    },
    setReserveResponse(state, reserveResponse) {
      state.reserveResponse = reserveResponse;
    },
    orderResults(state, orderType) {
      let sortPrice = (a, b) => {
        if (b.amount > a.amount) {
          return -1;
        } else if (b.amount < a.amount) {
          return 1;
        } else {
          return 0;
        }
      };
      let sortDuration = (a, b) => {
        let aLongestTime = undefined;
        let bLongestTime = undefined;
        for (let leg of a.odoList) {
          if (aLongestTime === undefined) {
            aLongestTime = leg.duration;
          } else if (aLongestTime < leg.duration) {
            aLongestTime = leg.duration;
          }
        }
        for (let leg of b.odoList) {
          if (bLongestTime === undefined) {
            bLongestTime = leg.duration;
          } else if (bLongestTime < leg.duration) {
            bLongestTime = leg.duration;
          }
        }
        if (bLongestTime > aLongestTime) {
          return -1;
        } else if (bLongestTime < aLongestTime) {
          return 1;
        } else {
          return 0;
        }
      };
      let sortDepartureTime = (a, b) => {
        if (
          asNumberTime(b.absoluteDepartureTime) >
          asNumberTime(a.absoluteDepartureTime)
        ) {
          return -1;
        } else if (
          asNumberTime(b.absoluteDepartureTime) <
          asNumberTime(a.absoluteDepartureTime)
        ) {
          return 1;
        } else {
          return 0;
        }
      };
      //baggage included filter
      let sortBaggageIncluded = (a, b) => {
        if (
          (!a.hasCheckedBagsForAllSegments || a.bagWarning) &&
          b.hasCheckedBagsForAllSegments &&
          !b.bagWarning
        ) {
          return 1;
        } else if (
          a.hasCheckedBagsForAllSegments &&
          !a.bagWarning &&
          (!b.hasCheckedBagsForAllSegments || b.bagWarning)
        ) {
          return -1;
        } else {
          return 0;
        }
      };
      if (orderType === "Price") {
        state.filteredOutbound = state.filteredOutbound.sort(sortPrice);
        if (state.flags.isDomesticReturn) {
          state.filteredReturn = state.filteredReturn.sort(sortPrice);
        }
      } else if (orderType === "Duration") {
        state.filteredOutbound = state.filteredOutbound.sort(sortDuration);
        if (state.flags.isDomesticReturn) {
          state.filteredReturn = state.filteredReturn.sort(sortDuration); //ask and fix
        }
      } else if (orderType === "Departure Time") {
        state.filteredOutbound = state.filteredOutbound.sort(sortDepartureTime);
        if (state.flags.isDomesticReturn) {
          state.filteredReturn = state.filteredReturn.sort(sortDepartureTime);
        }
      } else if (orderType === "Check Baggage Included") {
        state.filteredOutbound = state.filteredOutbound.sort(
          sortBaggageIncluded
        );
        if (state.flags.isDomesticReturn) {
          state.filteredReturn = state.filteredReturn.sort(sortBaggageIncluded);
        }
      }
    },
    filterFlights(state, filterOptions) {
      function isDirect(flight) {
        for (let option of flight.odoList) {
          if (option.segments.length > 1) {
            return false;
          }
        }
        return true;
      }
      function isCheckBaggageIncluded(flight) {
        if (!flight.hasCheckedBagsForAllSegments || flight.bagWarning) {
          return false;
        }
        return true;
      }
      function customOutboundFilter(flight) {
        if (
          (filterOptions.directOnly && !isDirect(flight)) ||
          (filterOptions.checkBaggageIncluded &&
            !isCheckBaggageIncluded(flight)) ||
          Number(flight.amount) < filterOptions.priceRange[0] ||
          Number(flight.amount) > filterOptions.priceRange[1] ||
          flight.biggestTravelTime < filterOptions.travelTime[0] * 60000 ||
          flight.biggestTravelTime > filterOptions.travelTime[1] * 60000 ||
          asNumberTime(flight.absoluteDepartureTime) <
            filterOptions.outboundTime[0] ||
          asNumberTime(flight.absoluteDepartureTime) >
            filterOptions.outboundTime[1] ||
          !flight.marketingAirlines.some((airline) =>
            filterOptions.airlines.includes(airline)
          )
        ) {
          return false;
        }
        return true;
      }
      function customReturnFilter(flight) {
        if (
          (filterOptions.directOnly && !isDirect(flight)) ||
          (filterOptions.checkBaggageIncluded &&
            !isCheckBaggageIncluded(flight)) ||
          Number(flight.amount) < filterOptions.priceRange[0] ||
          Number(flight.amount) > filterOptions.priceRange[1] ||
          flight.biggestTravelTime < filterOptions.travelTime[0] * 60000 ||
          flight.biggestTravelTime > filterOptions.travelTime[1] * 60000 ||
          asNumberTime(flight.absoluteDepartureTime) <
            filterOptions.returnTime[0] ||
          asNumberTime(flight.absoluteDepartureTime) >
            filterOptions.returnTime[1] ||
          !flight.marketingAirlines.some((airline) =>
            filterOptions.airlines.includes(airline)
          )
        ) {
          return false;
        }
        return true;
      }

      // Apply filters and preserve tags for international flights
      const filteredOutbound = state.outbound.filter(customOutboundFilter);

      // Re-apply tag sorting for international flights after filtering
      if (state.isInternational) {
        state.filteredOutbound = sortTaggedFlights(filteredOutbound);
      } else {
        state.filteredOutbound = filteredOutbound;
      }

      if (state.flags.isDomesticReturn) {
        state.filteredReturn = state.return.filter(customReturnFilter);
      }
    },
  },
  actions: {
    invsyLink({ state }, url) {
      if (state.isIframe) {
        invsy.platform.navigateUrl(url, "_blank");
      } else {
        window.open(url, "_blank");
      }
    },
    async initializeInvsy(context) {
      if (window !== window.parent) {
        invsy.platform.init();
        invsy.platform.initSession();

        try {
          // Get the points data
          const accounts = await invsy.api.privateClient.pbsa.accounts.list();
          let rewardsAvailableBalance = 0;
          if (accounts.result.RewardsAvailableBalance) {
            rewardsAvailableBalance = accounts.result.RewardsAvailableBalance;
          }
          if (typeof rewardsAvailableBalance !== "number") {
            rewardsAvailableBalance = Number(rewardsAvailableBalance);
          }
          context.commit("setPoints", rewardsAvailableBalance);

          // Get the user data
          // const res = await invsy.data.getAllUserContext();
          let clientData = {};
          //if (res.result.AuthResponse.authRes.IsMasqueradedAsClient) {
          clientData = await invsy.api.privateClient.pbsa.client.get();
          context.commit("setInvsyUser", clientData.result);
          //} else {
          //  clientData = await invsy.data.getClientBasicDetails();
          //   context.commit("setInvsyUser", clientData.result);
          // }
        } catch (error) {
          console.error("Failed to get user context:", error);
        }
      } else {
        console.log("not in iframe");
      }
      //invsy.platform.interactions.update({headerApp:{src:'https://example.com',show:true,height:'108'}})
    },
    async fetchLocations(context) {
      let countries = await Api.countries();
      context.commit("setCountires", countries);
    },
    resetAll(context) {
      context.commit("resetSelectedFlights");
      context.commit("resetSearchResults");
      context.commit("resetSession");
      context.commit("resetResultConstants");
      context.commit("resetPriceResponse");
      context.commit("resetPaymentMethodResponse");
      context.commit("resetBookingResponse");
      context.commit("resetShowReturnFlightsFlag");
    },
  },
});

// Helper function to sort tagged flights
function sortTaggedFlights(flights) {
  const taggedFlights = flights.filter(
    (f) => f.isCheapest || f.isFastest || f.isRecommended
  );
  const untaggedFlights = flights.filter(
    (f) => !f.isCheapest && !f.isFastest && !f.isRecommended
  );

  // Sort tagged flights by priority (recommended first, then cheapest, then fastest)
  taggedFlights.sort((a, b) => {
    const aPriority =
      (a.isRecommended ? 3 : 0) +
      (a.isCheapest ? 2 : 0) +
      (a.isFastest ? 1 : 0);
    const bPriority =
      (b.isRecommended ? 3 : 0) +
      (b.isCheapest ? 2 : 0) +
      (b.isFastest ? 1 : 0);
    return bPriority - aPriority;
  });

  return [...taggedFlights, ...untaggedFlights];
}

function getTotalDurationMs(flight) {
  let totalMs = 0;
  if (flight && flight.odoList) {
    for (const leg of flight.odoList) {
      const timeString = leg.totalTime;
      const hoursMatch = timeString.match(/(\d+)h/);
      const minutesMatch = timeString.match(/(\d+)m/);

      const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

      totalMs += (hours * 60 + minutes) * 60 * 1000;
    }
  }
  return totalMs;
}

// returns a random ID of n chunks eg 2; bu4adu2r-uis34yd1
function generateRandomID(n) {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  let randomID = "";
  for (let i = 1; i < n; ++i) {
    randomID += s4() + "-";
  }
  randomID += s4();
  return randomID;
}

function isEmpty(obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
}

function asNumberTime(time) {
  return Math.round(moment.duration(time).asHours() * 1e2) / 1e2;
}
